import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { Modal } from '../../../components/common/Modal';
import { UnitToggle } from '../../../components/common/UnitToggle';
import { useWeightUnit } from '../../../hooks/useWeightUnit';
import { useLanguage } from '../../../features/language/useLanguage';
import {
  reportService,
  type InventoryItemType,
  type InventoryTrackingEvent,
} from '../../../services/report.service';

interface InventoryTrackingDialogProps {
  itemId: string | null;
  itemType: InventoryItemType | null;
  isOpen: boolean;
  onClose: () => void;
}

type TrackingFilter = 'all' | 'in' | 'out';

export const InventoryTrackingDialog: React.FC<InventoryTrackingDialogProps> = ({
  itemId,
  itemType,
  isOpen,
  onClose,
}) => {
  const { t } = useLanguage();
  const [events, setEvents] = useState<InventoryTrackingEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TrackingFilter>('all');
  const { unit, setUnit } = useWeightUnit('kg');

  useEffect(() => {
    if (isOpen && itemId && itemType) {
      let cancelled = false;
      setFilter('all');
      void (async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await reportService.getInventoryTracking(itemId, itemType);
          if (!cancelled) setEvents(response.events);
        } catch (err) {
          if (!cancelled) {
            console.error('Failed to load tracking events:', err);
            setError(t('reports.tracking.error'));
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    } else {
      setEvents([]);
      setError(null);
    }
  }, [isOpen, itemId, itemType, t]);

  if (!isOpen) return null;

  // Within one open dialog every event shares the same unit; derive it from the events.
  const eventUnit: 'kg' | 'pcs' = events.length > 0 ? events[0].unit : itemType === 'finished_good' ? 'pcs' : 'kg';
  const isPcs = eventUnit === 'pcs';

  const formatQuantity = (valueKg: number) => {
    if (!Number.isFinite(valueKg)) return '-';
    if (isPcs) {
      return Number.isInteger(valueKg) ? String(valueKg) : String(Number(valueKg.toFixed(3)));
    }
    if (unit === 'kg') return valueKg.toFixed(3);
    return String(Math.round(valueKg * 1000));
  };

  const unitLabel = isPcs ? t('inventory.basicInfo.units.pcs') : unit === 'kg' ? 'kg' : 'g';

  const filteredEvents = events.filter(event => filter === 'all' || event.direction === filter);

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const filterButtonClass = (active: boolean) =>
    `px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
      active ? 'bg-primary-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
    }`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('reports.tracking.title')} maxWidth="6xl">
      <div className="space-y-4" data-test-id="inventory-tracking-dialog">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              className={filterButtonClass(filter === 'all')}
              onClick={() => setFilter('all')}
              data-test-id="tracking-filter-all"
            >
              {t('reports.tracking.filterAll')}
            </button>
            <button
              type="button"
              className={filterButtonClass(filter === 'in')}
              onClick={() => setFilter('in')}
              data-test-id="tracking-filter-in"
            >
              {t('reports.tracking.filterIn')}
            </button>
            <button
              type="button"
              className={filterButtonClass(filter === 'out')}
              onClick={() => setFilter('out')}
              data-test-id="tracking-filter-out"
            >
              {t('reports.tracking.filterOut')}
            </button>
          </div>
          {!isPcs && <UnitToggle unit={unit} onToggle={setUnit} className="shrink-0" />}
        </div>

        <div className="mt-2">
          {loading ? (
            <LoadingSpinner text={t('forms.common.loadingHistory')} size="sm" />
          ) : error ? (
            <div className="text-red-600 text-sm py-2">{error}</div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-gray-500 text-sm py-4">{t('reports.tracking.empty')}</div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.tracking.table.date')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.tracking.table.direction')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.tracking.table.reference')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.tracking.table.counterparty')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.tracking.table.lot')}</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.tracking.table.quantity')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEvents.map((event, index) => {
                    const isIn = event.direction === 'in';
                    const directionLabel = isIn ? t('reports.tracking.directionIn') : t('reports.tracking.directionOut');
                    return (
                      <tr key={`${event.date}-${event.referenceLabel}-${index}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(event.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              isIn ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {directionLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {event.referenceLabel || t('settingsMcp.values.none')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {event.counterparty || t('settingsMcp.values.none')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {event.lotNumber || '—'}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                            isIn ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {isIn ? '+' : '−'}
                          {formatQuantity(event.quantity)} {unitLabel}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 sm:mt-6 flex justify-end">
        <button
          type="button"
          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
          onClick={onClose}
        >
          {t('forms.actions.close')}
        </button>
      </div>
    </Modal>
  );
};
